import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Store } from 'src/store';
import sendPostMessage from 'src/store/app/operation/sendPostMessage';
import { MESSAGE_TYPE } from 'src/store/app/types';
import fetchPricesOperation from 'src/store/price/operation/fetchPricesOperation';
import { openBatchSelectionAction } from 'src/store/multisize/slice';
import { buildBatchSelectionRows, computeBatchSelectionTotals } from 'src/util/apparel/batchSelection';

const PRICE_FETCH_CHUNK_SIZE = 18;

/**
 * OP-40008: open the editor-rendered batch selection modal instead of the
 * host-side drawer. Pushes the rows plus fully formatted footer strings, then
 * asks the editor to show the modal; every quantity change comes back as
 * batchSelection.selectionChanged so pricing stays entirely host-side.
 */
const openBatchSelectionModalOperation = () => async (dispatch: ThunkDispatch<Store, undefined, AnyAction>, getState: () => Store) => {
  const { rows, groups, productIds } = buildBatchSelectionRows(getState());
  const categoryId = getState().design.categoryId || getState().design.abstractProduct?.categoryId || 0;

  // Totals need per-quantity price tiers for every available variant. Fetched
  // in parallel chunks — one request for the full colour×size matrix is heavy
  // enough to time out on the backend. allSettled, not all: a failed chunk
  // leaves those rows priced as "—" (knownPrices=false), which still lets the
  // customer select, rather than never opening the modal at all.
  const uniqueProductIds = [...new Set(productIds)];
  if (categoryId && uniqueProductIds.length > 0) {
    const chunks: number[][] = [];
    for (let i = 0; i < uniqueProductIds.length; i += PRICE_FETCH_CHUNK_SIZE) {
      chunks.push(uniqueProductIds.slice(i, i + PRICE_FETCH_CHUNK_SIZE));
    }
    await Promise.allSettled(chunks.map((chunk) => dispatch(fetchPricesOperation(categoryId, chunk))));
  }

  const initialItems = rows
    .filter((r) => r.available && r.initialQuantity > 0)
    .map((r) => ({ id: r.id, quantity: r.initialQuantity }));

  // Mark the session open before handing over: inbound batchSelection messages
  // are only acted on while the host itself has a modal in flight.
  dispatch(openBatchSelectionAction());

  // setData both delivers the payload and opens the modal — the host sends it
  // exactly when the modal should show; later totals refreshes go via setTotals.
  sendPostMessage({
    type: MESSAGE_TYPE['batchSelection.setData'],
    data: {
      title: 'Size and quantity',
      confirmText: 'Add to basket',
      unavailableText: 'Unavailable',
      rows,
      groups,
      totals: computeBatchSelectionTotals(getState(), initialItems),
    },
  });
};

export default openBatchSelectionModalOperation;
